import type { FlightActivity } from '@/lib/builder/models/activity.model';
import type { FlightLookupEndpoint, ResolvedFlightLookup } from '@/types/flight.types';

type FlightTravel = FlightActivity['travels'][0];

/**
 * Spezza un ISO 8601 in data e ora **senza** passare da `new Date()`.
 *
 * Gli orari di volo sono ora locale dello scalo: convertirli nel fuso del browser
 * sposterebbe un decollo delle 07:05 da Bergamo alle 09:05 per un TD a Dubai.
 * Si leggono quindi i caratteri della stringa cosi' come sono.
 */
export const splitScheduledTime = (iso?: string | null): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' };

  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (match) {
    return { date: match[1], time: `${match[2]}:${match[3]}` };
  }

  // Formato inatteso: si tenta il parse standard, ma senza pretese sul fuso.
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return { date: '', time: '' };

  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
  };
};

/**
 * Compone l'etichetta aeroporto nello stesso formato di `src/components/utils/airports.ts`
 * — "Nome Aeroporto (IATA) - Citta'" — cosi' un aeroporto arrivato dal lookup e uno
 * scelto a mano dall'autocomplete restano indistinguibili nel campo `from`/`to`.
 *
 * Il contratto non espone il paese: l'etichetta si ferma alla citta'.
 */
export const formatAirportLabel = (endpoint: FlightLookupEndpoint): string => {
  const name = endpoint.airportName?.trim() || endpoint.airportIata?.trim() || '';
  const iata = endpoint.airportIata?.trim();
  const city = endpoint.city?.trim();

  const head = iata && !name.includes(`(${iata})`) ? `${name} (${iata})` : name;
  return city ? `${head} - ${city}` : head;
};

/**
 * Traduce il risultato del lookup nei campi della tratta.
 *
 * Ritorna **solo** i campi che il lookup possiede: chi applica il risultato fa il
 * merge sulla tratta esistente, cosi' link, note e costi non vengono toccati.
 */
export const resolvedFlightToTravelFields = (resolved: ResolvedFlightLookup): Partial<FlightTravel> => {
  const { flight, airline } = resolved;
  const departure = splitScheduledTime(flight.departure.scheduledTime);
  const arrival = splitScheduledTime(flight.arrival.scheduledTime);

  return {
    flightNumber: flight.flight.iata || `${airline.iata}${flight.flight.number}`,
    airline: airline.name,
    from: formatAirportLabel(flight.departure),
    to: formatAirportLabel(flight.arrival),
    fromDate: departure.date,
    fromTime: departure.time,
    toDate: arrival.date,
    toTime: arrival.time,
    airlineIata: airline.iata,
    airlineLogoUrl: airline.logoUrl ?? undefined,
    airlineColor: airline.color ?? undefined,
    fromCode: flight.departure.airportIata || undefined,
    toCode: flight.arrival.airportIata || undefined,
    fromTerminal: flight.departure.terminal ?? undefined,
    fromGate: flight.departure.gate ?? undefined,
    toTerminal: flight.arrival.terminal ?? undefined,
    toGate: flight.arrival.gate ?? undefined,
    flightStatus: flight.flight.status || undefined,
    aircraftModel: flight.aircraft?.model ?? undefined,
  };
};

/** Una tratta e' "vuota" se il TD non ci ha ancora scritto nulla di identificativo. */
export const isTravelEmpty = (travel?: Partial<FlightTravel>): boolean => {
  if (!travel) return true;
  return !travel.flightNumber?.trim() && !travel.airline?.trim() && !travel.from?.trim() && !travel.to?.trim();
};
